import './lohn.css';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fmtDate, fmtDayMonth, fmtTime, matchesName, monthName, MONTHS, plural, SEARCH_FROM } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { EmployeeDialog } from './EmployeeDialog';
import { recognize } from './erkennen';
import { entryPill } from './entry';
import { ImportDialog } from './ImportDialog';
import { LOHN_KIND_LABEL, LOHN_KINDS, type LohnDoc, type LohnKind, type LohnTabResponse } from './types';

// Tab „Lohn“ eines Mandanten (Lohn-Zuständige und Admin): Mitarbeiter mit Personalakte und Lohnunterlagen.
// Eine Personalakte öffnet sich über ?ma=<id>, damit „Ansehen“ aus der Hinweisleiste direkt dorthin führt.
export function LohnTab({ clientId }: { clientId: string }) {
  const [params, setParams] = useSearchParams();
  const res = useApi<LohnTabResponse>(`/api/kanzlei/lohn/${clientId}`, { live: ['lohn'], clientId });
  const open = params.get('ma');
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState('');
  // viele Mitarbeiter: Liste eingeklappt (Sam 12.10.), sichtbar bleibt, wer etwas zu tun hat
  const [expanded, setExpanded] = useState(false);
  const d = res.data;

  const show = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('ma', id);
    else next.delete('ma');
    setParams(next, { replace: true });
  };

  if (!d) return res.error ? <p className="note">{res.error}</p> : <div className="spinner" aria-label="Lädt" />;
  const neu = d.employees.filter((e) => e.status === 'neu').length;
  const abgemeldet = d.employees.filter((e) => e.abgemeldet || e.gone).length;
  const many = d.employees.length >= SEARCH_FROM;
  const searching = !!query.trim();
  const listOpen = !many || expanded || searching;
  const todo = (e: (typeof d.employees)[number]) => e.status === 'neu' || e.openChanges > 0 || e.termination?.status === 'offen';
  const shown = d.employees.filter((e) => (listOpen ? matchesName(e.name, query) : todo(e)));

  return (
    <div className="lohn">
      <div className="card">
        <div className="ch">
          <h3>Mitarbeiter</h3>
          <span>
            {neu > 0 && `${neu} neu gemeldet · `}
            {d.employees.length - neu - abgemeldet} angemeldet
            {abgemeldet > 0 && ` · ${abgemeldet} abgemeldet`}
          </span>
          {many && !searching && (
            <button type="button" className="btn sm" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Zuklappen ▴' : `Alle ${d.employees.length} anzeigen ▾`}
            </button>
          )}
        </div>
        {many && (
          <input
            type="search"
            className="find"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Mitarbeiter suchen (Name)"
            aria-label="Mitarbeiter suchen"
          />
        )}
        {searching && !shown.length && <p className="note">Kein Mitarbeiter mit „{query.trim()}“ gefunden.</p>}
        {d.employees.length ? (
          shown.map((e) => {
            const pill = entryPill(e.entryDate, e.status !== 'neu');
            const t = e.termination;
            return (
              <div className="task" key={e.id}>
                <span className="av" aria-hidden="true">
                  👤
                </span>
                <div className="tx">
                  <b>{e.name}</b>{' '}
                  {e.status === 'neu' ? (
                    <span className="pill p-info">neu gemeldet</span>
                  ) : e.abgemeldet ? (
                    <span className="pill p-neu">abgemeldet</span>
                  ) : e.gone ? (
                    <span className="pill p-neu">ausgeschieden</span>
                  ) : (
                    <span className="pill p-ok">angemeldet</span>
                  )}
                  {e.status === 'neu' && (
                    <>
                      {' '}
                      <span className={`pill p-${pill.tone}`}>{pill.label}</span>
                    </>
                  )}
                  {e.openChanges > 0 && (
                    <>
                      {' '}
                      <span className="pill p-info">
                        {e.openChanges === 1 ? 'Änderung gemeldet' : `${e.openChanges} Änderungen gemeldet`}
                      </span>
                    </>
                  )}
                  {t?.status === 'offen' && (
                    <>
                      {' '}
                      <span className="pill p-warn">Kündigung gemeldet</span>
                    </>
                  )}
                  <small>
                    {[e.job, e.kind].filter(Boolean).join(' · ')}
                    {e.status !== 'neu' && ` · Eintritt ${fmtDate(e.entryDate)}`}
                    {t && ` · Austritt ${e.gone ? '' : 'am '}${fmtDate(t.lastDay)}`}
                  </small>
                </div>
                <button type="button" className={`btn sm${todo(e) ? ' dark' : ''}`} onClick={() => show(e.id)}>
                  Ansehen
                </button>
              </div>
            );
          })
        ) : (
          <p className="note">
            Noch keine Mitarbeiter. {d.client.contactName} legt neue Mitarbeiter in der App an, sie erscheinen dann hier. Wechselt
            der Mandant mit seinen Mitarbeitern zu uns, übernehmen Sie hier seine Liste.
          </p>
        )}
        <div className="imp-open">
          <button type="button" className="btn sm" onClick={() => setImporting(true)}>
            Mitarbeiter übernehmen (Excel oder PDF)
          </button>
        </div>
      </div>
      <div className="grid2">
        <UploadCard d={d} reload={res.reload} />
        <DocsCard d={d} reload={res.reload} />
      </div>
      {open && <EmployeeDialog key={open} id={open} onClose={() => show(null)} />}
      {importing && (
        <ImportDialog
          clientId={d.client.id}
          contactName={d.client.contactName}
          ki={d.ki}
          kiTest={d.kiTest}
          kiAus={d.kiAus}
          onClose={() => setImporting(false)}
          onDone={() => void res.reload()}
        />
      )}
    </div>
  );
}

// Lohnunterlagen hochladen: Jahr und Monat wählen, Dateien wählen. Die Art erkennt die App je Datei selbst
// (erkennen.ts), die Kanzlei sieht sie vorher und kann sie ändern; nicht erkannte wählt sie von Hand.
interface Pick {
  key: string;
  file: File;
  kind: LohnKind | '';
  state: 'lesen' | 'erkannt' | 'offen' | 'gewaehlt';
}

function UploadCard({ d, reload }: { d: LohnTabResponse; reload(): Promise<void> }) {
  const toast = useToast();
  const prev = d.today.month === 1 ? { year: d.today.year - 1, month: 12 } : { year: d.today.year, month: d.today.month - 1 };
  const [year, setYear] = useState(prev.year);
  const [month, setMonth] = useState(prev.month);
  const [files, setFiles] = useState<Pick[]>([]);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const future = (y: number, m: number) => y > d.today.year || (y === d.today.year && m > d.today.month);
  const update = (key: string, patch: Partial<Pick>) =>
    setFiles((list) => list.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const add = (list: FileList | null) => {
    if (!list?.length) return;
    const known = new Set(files.map((f) => f.key));
    const fresh: Pick[] = [];
    for (const file of Array.from(list)) {
      const key = `${file.name}|${file.size}`;
      if (known.has(key)) continue;
      known.add(key);
      fresh.push({ key, file, kind: '', state: 'lesen' });
    }
    setFiles((old) => [...old, ...fresh]);
    // nacheinander lesen, damit der Browser bei vielen PDFs nicht hängt
    void (async () => {
      for (const f of fresh) {
        const kind = await recognize(f.file).catch(() => null);
        setFiles((old) =>
          old.map((x) =>
            x.key === f.key && x.state === 'lesen' ? { ...x, kind: kind ?? '', state: kind ? 'erkannt' : 'offen' } : x,
          ),
        );
      }
    })();
  };

  const send = async () => {
    const open = files.filter((f) => !f.kind);
    if (open.length) {
      toast(
        files.some((f) => f.state === 'lesen')
          ? 'Einen Moment, die Dateien werden noch gelesen.'
          : `Bitte bei ${plural(open.length, 'Datei', 'Dateien')} die Art wählen.`,
      );
      document.getElementById(`lk-${files.indexOf(open[0]!)}`)?.focus();
      return;
    }
    setBusy(true);
    const fd = new FormData();
    for (const f of files) fd.append(f.kind, f.file, f.file.name);
    try {
      const r = await api.post<{ docs: { id: string }[]; errors: { name: string; error: string }[] }>(
        `/api/kanzlei/lohn/${d.client.id}/docs/${year}/${month}`,
        fd,
      );
      setFiles([]);
      await reload();
      const n = r.docs.length;
      toast(
        r.errors.length
          ? `${plural(n, 'Datei', 'Dateien')} hochgeladen · nicht gespeichert: ${r.errors.map((e) => e.name || e.error).join(', ')}`
          : `${n === 1 ? 'Datei' : `${n} Dateien`} hochgeladen · ${d.client.contactName} sieht sie in der App`,
      );
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const where = `Lohn › ${monthName(month)} ${year}`;
  return (
    <div className="card">
      <div className="ch">
        <h3>Lohnunterlagen hochladen</h3>
        <span>erscheint sofort in der App</span>
      </div>
      <div className="form">
        <div className="row">
          <label>
            Jahr
            <select
              value={year}
              onChange={(e) => {
                const y = Number(e.target.value);
                setYear(y);
                if (future(y, month)) setMonth(d.today.month);
              }}
            >
              {d.years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label>
            Monat
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((n, i) => (
                <option key={n} value={i + 1} disabled={future(year, i + 1)}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        {files.length ? (
          <div className="confirmup">
            <b>{files.length === 1 ? 'Diese Datei hochladen?' : `Diese ${files.length} Dateien hochladen?`}</b>
            <div className="lk-list">
              {files.map((f, i) => (
                <div className="lk" key={f.key}>
                  <span className="lk-name">📄 {f.file.name}</span>
                  <button
                    type="button"
                    className="linkbtn"
                    aria-label={`${f.file.name} aus der Auswahl nehmen`}
                    onClick={() => setFiles(files.filter((x) => x.key !== f.key))}
                  >
                    ×
                  </button>
                  <span className="lk-kind">
                    <select
                      id={`lk-${i}`}
                      aria-label={`Art von ${f.file.name}`}
                      value={f.kind}
                      disabled={f.state === 'lesen'}
                      onChange={(e) => update(f.key, { kind: e.target.value as LohnKind | '', state: 'gewaehlt' })}
                    >
                      <option value="">{f.state === 'lesen' ? 'Wird gelesen …' : 'Bitte wählen'}</option>
                      {LOHN_KINDS.map((k) => (
                        <option key={k.key} value={k.key}>
                          {k.label}
                        </option>
                      ))}
                    </select>
                    {f.state === 'erkannt' && <span className="pill p-ok">erkannt</span>}
                    {f.state === 'offen' && <span className="pill p-warn">nicht erkannt</span>}
                  </span>
                </div>
              ))}
            </div>
            <span className="note">
              Ablage: {where}. Die Art hat die App aus dem Inhalt und dem Dateinamen erkannt, bitte kurz prüfen. Danach sieht{' '}
              {d.client.contactName} {files.length === 1 ? 'die Datei' : 'die Dateien'} in der App.
            </span>
            {files.some((f) => f.kind === 'lst_freigabe') && (
              <span className="note">
                Zur Freigabe: {d.client.contactName} prüft die Lohnsteuer-Anmeldung und drückt „Freigeben“. Bitte erst danach an
                das Finanzamt übermitteln.
              </span>
            )}
            <div className="bk-actions">
              <button type="button" className="btn sm dark" disabled={busy} onClick={send}>
                {files.length === 1 ? 'Hochladen' : `${files.length} Dateien hochladen`}
              </button>
              <button type="button" className="btn sm" onClick={() => pick.current?.click()}>
                Weitere hinzufügen
              </button>
              <button type="button" className="btn sm" onClick={() => setFiles([])}>
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={`drop${over ? ' over' : ''}`}
            onClick={() => pick.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              add(e.dataTransfer.files);
            }}
          >
            <b>PDFs hierher ziehen oder klicken</b>
            <small>Mehrere Dateien auf einmal · die Art erkennt die App selbst · Ablage: {where}</small>
          </button>
        )}
        <input
          ref={pick}
          type="file"
          multiple
          accept="application/pdf,image/*"
          hidden
          data-testid="lohn-pick"
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

// Hochgeladene Lohnunterlagen, neuester Monat oben. Löschen nach Rückfrage.
function DocsCard({ d, reload }: { d: LohnTabResponse; reload(): Promise<void> }) {
  const toast = useToast();
  const [ask, setAsk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const months: { key: string; year: number; month: number; docs: LohnDoc[] }[] = [];
  for (const doc of d.docs) {
    const key = `${doc.year}-${doc.month}`;
    let m = months.find((x) => x.key === key);
    if (!m) months.push((m = { key, year: doc.year, month: doc.month, docs: [] }));
    m.docs.push(doc);
  }

  const del = async (doc: LohnDoc) => {
    setBusy(true);
    try {
      await api.del(`/api/kanzlei/lohn-doc/${doc.id}`);
      setAsk(null);
      await reload();
      toast(`Gelöscht: ${doc.name}`);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="ch">
        <h3>Hochgeladen</h3>
        <span>{plural(d.docs.length, 'Datei', 'Dateien')}</span>
      </div>
      {!months.length && <p className="note">Noch keine Lohnunterlagen hochgeladen.</p>}
      {months.map((m) => (
        <div key={m.key} className="lmonth">
          <h4>
            {monthName(m.month)} {m.year}
          </h4>
          <div className="ul">
            {m.docs.map((doc) =>
              ask === doc.id ? (
                <div key={doc.id} className="ask">
                  <span>
                    {doc.name} wirklich löschen? {d.client.contactName} sieht die Datei dann nicht mehr.
                  </span>
                  <span className="bk-actions">
                    <button type="button" className="btn sm dark" disabled={busy} onClick={() => del(doc)}>
                      Ja, löschen
                    </button>
                    <button type="button" className="btn sm" onClick={() => setAsk(null)}>
                      Abbrechen
                    </button>
                  </span>
                </div>
              ) : (
                <div key={doc.id}>
                  <span>
                    <a href={`/api/kanzlei/lohn-doc/${doc.id}/file`} target="_blank" rel="noreferrer">
                      {doc.name}
                    </a>
                    <span className="sub">
                      {LOHN_KIND_LABEL[doc.kind]} · {fmtDayMonth(doc.createdAt)}
                      {doc.uploadedBy ? ` · ${doc.uploadedBy}` : ''}
                    </span>
                    {doc.freigabe === null && (
                      <span className="sub">
                        <span className="pill p-warn">wartet auf Freigabe</span>
                      </span>
                    )}
                    {doc.freigabe && (
                      <span className="sub">
                        <span className="pill p-ok">freigegeben</span> von {doc.freigabe.name} am {fmtDate(doc.freigabe.at)} um{' '}
                        {fmtTime(doc.freigabe.at)} Uhr
                      </span>
                    )}
                    {doc.korrektur && (
                      <span className="sub">
                        {doc.korrektur.hidden
                          ? `${d.client.contactName} sieht sie nicht mehr: Alle Abrechnungen darin sind korrigiert, zuletzt am ${fmtDate(doc.korrektur.at)}.`
                          : `${d.client.contactName} sieht sie ohne ${doc.korrektur.names.join(', ')}: korrigiert am ${fmtDate(doc.korrektur.at)}.`}
                      </span>
                    )}
                  </span>
                  <button type="button" className="del" aria-label={`${doc.name} löschen`} onClick={() => setAsk(doc.id)}>
                    Löschen
                  </button>
                </div>
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
